/*
 * AssignShiftModal — one-click guard assignment triggered from the Tardies
 * page for open (unassigned) shifts. Talks to `POST /shifts/{id}/assign`
 * which enforces active-guard + weekly-hours guardrails without requiring
 * the full ShiftIn payload.
 */
import { useEffect, useMemo, useState } from "react";
import api, { errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { Modal } from "../Modal";
import { GhostButton } from "../Primitives";
import { MultiSelectDropdown } from "../MultiSelectDropdown";
import { MapPin, Clock, Users, Loader2 } from "lucide-react";

export function AssignShiftModal({ row, onClose, onAssigned }) {
  const [guards, setGuards] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/guards")
      .then((r) => { if (!cancelled) setGuards((r.data || []).filter((g) => g.status !== "inactive")); })
      .catch((e) => { if (!cancelled) toast.error(errorMessage(e, "Failed to load ")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const options = useMemo(
    () => guards.map((g) => ({
      value: g.id,
      label: g.name,
      sub: [g.badge_no && `#${g.badge_no}`, g.role].filter(Boolean).join(" · "),
    })).sort((a, b) => a.label.localeCompare(b.label)),
    [guards]
  );

  const save = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one ");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/shifts/${row.shift_id}/assign`, { guard_ids: selected });
      toast.success(`Assigned ${selected.length} guard${selected.length > 1 ? "s" : ""} to ${row.site_name || "shift"}`);
      onAssigned?.();
      onClose?.();
    } catch (e) {
      toast.error(errorMessage(e, "Assignment failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Assign Officer to Open Shift" onClose={onClose} testId="assign-shift-modal">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm border border-[#27272A] bg-[#0A0A0A] p-3">
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><MapPin className="size-3" /> Site</div>
            <div className="text-zinc-100 font-medium truncate">{row.site_name || "—"}</div>
            {row.client_name && <div className="text-xs text-zinc-500 truncate">{row.client_name}</div>}
          </div>
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><Clock className="size-3" /> Shift start</div>
            <div className="font-mono text-xs text-zinc-100">{new Date(row.shift_start).toLocaleString()}</div>
            <div className="font-mono text-[11px] text-[#FF3B30] mt-0.5">{row.minutes_late} min late</div>
          </div>
        </div>

        <div>
          <div className="label-mute mb-1.5 flex items-center gap-1"><Users className="size-3" /> Officers *</div>
          {loading ? (
            <div className="border border-[#27272A] bg-[#0A0A0A] px-3 py-4 text-xs text-zinc-500 flex items-center gap-2">
              <Loader2 className="size-3 animate-spin" /> Loading …
            </div>
          ) : (
            <MultiSelectDropdown
              testId="assign-shift-guards"
              label={selected.length ? `${selected.length} selected` : "Pick guards…"}
              options={options}
              selected={selected}
              onChange={setSelected}
              searchable
              triggerClassName="w-full"
              emptyHint="No active "
            />
          )}
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
            The backend still enforces active- checks and the weekly-hours cap.
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-[#27272A]">
          <GhostButton onClick={onClose} disabled={saving} data-testid="assign-shift-cancel">Cancel</GhostButton>
          <button
            type="button"
            data-testid="assign-shift-save"
            className="uppercase tracking-widest text-xs font-bold px-4 py-2 border border-[#FF3B30] bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90 disabled:opacity-50"
            disabled={saving || loading || selected.length === 0}
            onClick={save}
          >
            {saving ? "Assigning…" : "Assign & Notify"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
